import {useCallback} from 'react';

import {type MarkAllReadResponse} from '@onroadvantage/optix-safeandsound-openapi';
import {useMutation, useQueryClient} from '@tanstack/react-query';

import {
  NOTIFICATIONS_LIST_QUERY_KEY,
  UNREAD_COUNT_QUERY_KEY,
} from './notificationQueryKeys';
import {notificationsApi} from '../../../../api';
import {toast} from '../../../../ui-lib/components';
import {parseApiError} from '../../utils/parseApiError';

export interface UseNotificationMutationsReturn {
  markRead: (id: string) => void;
  markAllRead: () => void;
  archive: (id: string) => void;
  isMarkingAllRead: boolean;
}

export const useNotificationMutations = (): UseNotificationMutationsReturn => {
  const queryClient = useQueryClient();

  const invalidateNotificationQueries = useCallback((): void => {
    void queryClient.invalidateQueries({queryKey: UNREAD_COUNT_QUERY_KEY});
    void queryClient.invalidateQueries({
      queryKey: NOTIFICATIONS_LIST_QUERY_KEY,
    });
  }, [queryClient]);

  const handleError = useCallback((error: Error): void => {
    void (async (): Promise<void> => {
      const parsedError = await parseApiError(error);
      toast(parsedError.message, 'error');
    })();
  }, []);

  const markReadMutation = useMutation({
    mutationFn: async (id: string): Promise<void> => {
      await notificationsApi.markRead({id});
    },
    onSuccess: invalidateNotificationQueries,
    onError: handleError,
  });

  const markAllReadMutation = useMutation({
    mutationFn: async (): Promise<MarkAllReadResponse> => {
      return await notificationsApi.markAllRead();
    },
    onSuccess: invalidateNotificationQueries,
    onError: handleError,
  });

  const archiveMutation = useMutation({
    mutationFn: async (id: string): Promise<void> => {
      await notificationsApi.archive({id});
    },
    onSuccess: invalidateNotificationQueries,
    onError: handleError,
  });

  const {mutate: mutateMarkRead} = markReadMutation;
  const {mutate: mutateMarkAllRead} = markAllReadMutation;
  const {mutate: mutateArchive} = archiveMutation;

  const markRead = useCallback(
    (id: string): void => {
      mutateMarkRead(id);
    },
    [mutateMarkRead],
  );

  const markAllRead = useCallback((): void => {
    mutateMarkAllRead();
  }, [mutateMarkAllRead]);

  const archive = useCallback(
    (id: string): void => {
      mutateArchive(id);
    },
    [mutateArchive],
  );

  return {
    markRead,
    markAllRead,
    archive,
    isMarkingAllRead: markAllReadMutation.isPending,
  };
};
